import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Imperative-style confirm dialog using shadcn AlertDialog.
 *
 * Wrap your app with <ConfirmDialogProvider>, then call useConfirm() to get
 * an `async confirm({...})` function that resolves to true/false.
 *
 * Replaces window.confirm() for visual consistency.
 */
const ConfirmContext = createContext(null);

const DEFAULTS = {
    title: "Are you sure?",
    description: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    destructive: false,
};

export function ConfirmDialogProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [opts, setOpts] = useState(DEFAULTS);
    const resolverRef = useRef(null);

    const confirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            setOpts({ ...DEFAULTS, ...options });
            setOpen(true);
            resolverRef.current = resolve;
        });
    }, []);

    const finish = (value) => {
        setOpen(false);
        if (resolverRef.current) {
            resolverRef.current(value);
            resolverRef.current = null;
        }
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <AlertDialog
                open={open}
                onOpenChange={(o) => {
                    if (!o) finish(false);
                }}
            >
                <AlertDialogContent className="rounded-none border border-slate-200" data-testid="confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display tracking-tight">
                            {opts.title}
                        </AlertDialogTitle>
                        {opts.description && (
                            <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">
                                {opts.description}
                            </AlertDialogDescription>
                        )}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={() => finish(false)}
                            data-testid="confirm-dialog-cancel"
                            className="rounded-none border-slate-300"
                        >
                            {opts.cancelText}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => finish(true)}
                            data-testid="confirm-dialog-action"
                            className={`rounded-none ${
                                opts.destructive
                                    ? "bg-red-700 hover:bg-red-800 text-white"
                                    : "bg-slate-900 hover:bg-slate-800 text-white"
                            }`}
                        >
                            {opts.confirmText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        // Fallback to native confirm so non-wrapped trees still work
        return async (opts) => window.confirm(opts?.description || opts?.title || "Are you sure?");
    }
    return ctx;
}
